/**
 * TRENDS TAB
 *
 * Composes three views of the same daily-bucketed execution data:
 *   - Pass rate over time (line)
 *   - Execution volume by status (stacked area)
 *   - Execution velocity (bar)
 *
 * Accepts `level` + `id` and fetches from `fetchTrendsAsync`. Reacts to the
 * shared filter bar — when filters change in Redux, the effect re-fetches.
 */

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from '@atlaskit/spinner'
import { token } from '@atlaskit/tokens'

import TrendLineChart from '../charts/TrendLineChart'
import StatusStackedAreaChart from '../charts/StatusStackedAreaChart'
import VelocityBarChart from '../charts/VelocityBarChart'
import {
  fetchTrendsAsync,
  selectTrends,
  selectReportFilters,
  selectReportLoading,
  selectReportErrors
} from '../../../store/slices/reportsSlice'

const SectionHeading = ({ title, subtitle }) => (
  <div style={{ marginBottom: '8px' }}>
    <div
      style={{
        fontSize: '14px',
        fontWeight: 600,
        color: token('color.text', '#172B4D')
      }}
    >
      {title}
    </div>
    {subtitle && (
      <div
        style={{
          fontSize: '12px',
          color: token('color.text.subtle', '#6B778C'),
          marginTop: '2px'
        }}
      >
        {subtitle}
      </div>
    )}
  </div>
)

const TrendsTab = ({ level, id }) => {
  const dispatch = useDispatch()
  const trends = useSelector(selectTrends)
  const filters = useSelector(selectReportFilters)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  // Re-fetch whenever scope or filters change. JSON.stringify is fine here —
  // filters is a shallow object of small arrays.
  const filterKey = JSON.stringify(filters)

  useEffect(() => {
    if (id && level) {
      dispatch(fetchTrendsAsync({ level, id, filters }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, level, id, filterKey])

  if (loading.trends) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spinner size="large" />
      </div>
    )
  }

  if (errors.trends) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          color: token('color.text.danger', '#DE350B')
        }}
      >
        <p>Failed to load trend data</p>
        <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>
          {errors.trends}
        </p>
      </div>
    )
  }

  const buckets = trends?.buckets || []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <section>
        <SectionHeading
          title="Pass rate over time"
          subtitle="Daily pass rate; dashed line is the period average."
        />
        <TrendLineChart buckets={buckets} height={240} />
      </section>

      <section>
        <SectionHeading
          title="Executions by status"
          subtitle="Stacked volume so you can see where the bulk of outcomes fall each day."
        />
        <StatusStackedAreaChart buckets={buckets} height={240} />
      </section>

      <section>
        <SectionHeading
          title="Execution volume"
          subtitle="Total executions per day — test activity regardless of outcome."
        />
        <VelocityBarChart buckets={buckets} height={200} />
      </section>
    </div>
  )
}

export default TrendsTab
